import React from "react";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import HandymanIcon from "@mui/icons-material/Handyman";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";

// Styled components with responsive adjustments
const MaintenanceCard = styled(Card)(({ theme }) => ({
  padding: "4em",
  maxWidth: 320,
  minWidth: 200,
  margin: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
  borderRadius: "12px",
  marginTop: "9em",
  backgroundColor: "#ffffff",
  transition: "box-shadow 0.3s ease-in-out",
  position: "relative",
  "&:hover": {
    boxShadow: "0 12px 28px rgba(0, 0, 0, 0.3)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "2.5em",
    marginTop: "5em",
    maxWidth: "85%",
  },
}));

const MaintenanceTitle = styled(Typography)(({ theme }) => ({
  marginTop: "1em",
  fontSize: "28px",
  fontWeight: "700", // Bold
  color: "#ff5233", // Bright red for better visibility
  textAlign: "center",
  letterSpacing: "0.5px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "24px",
    marginTop: "0.5em",
  },
}));

const MaintenanceMessage = styled(Typography)(({ theme }) => ({
  marginTop: "1em",
  fontSize: "16px", // Larger for better readability
  color: "#333", // Darker color for better contrast
  textAlign: "center",
  lineHeight: 1.6,
  fontWeight: "400",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    marginTop: "0.8em",
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  width: "90px",
  height: "90px",
  background: "#f0f5ff", // Light blue background
  borderRadius: "50%", // Circle
  marginBottom: "15px",
  marginTop: "10px",
  [theme.breakpoints.down("sm")]: {
    width: "70px",
    height: "70px",
    marginBottom: "10px",
  },
}));

const MaintenancePeriod = styled(Typography)(({ theme }) => ({
  marginTop: "1.5em",
  fontSize: "14px",
  color: "#555", // Darker for better visibility
  textAlign: "center",
  padding: "8px 15px",
  backgroundColor: "#f0f5ff", // Match the icon container
  borderRadius: "20px",
  border: "1px solid #dbe7ff",
  fontWeight: "500", // Medium weight for better visibility
  whiteSpace: "nowrap", // Keep on one line
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    marginTop: "1.2em",
    padding: "6px 12px",
  },
}));

// Container for the whole page
const MaintenanceContainer = styled(Box)(({ theme }) => ({
  overflow: "hidden",
  width: "100vw",
  height: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  backgroundSize: "cover",
  backgroundColor: "rgba(0, 0, 0, 0.4)", // Darker grey overlay
  backgroundBlendMode: "overlay", // Blend the background image with grey
  [theme.breakpoints.down("sm")]: {
    alignItems: "center", // Center vertically on mobile
  },
}));

// Logo container with responsive sizing
const LogoContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  marginBottom: "2em",
  display: "flex",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    marginBottom: "1.5em",
  },
}));

/**
 * Responsive Maintenance Page Component that matches login page styling
 *
 * @param {Object} props - Component props
 * @param {string} [props.title="Under maintenance"] - Title for the maintenance notice
 * @param {string} [props.message="Our engineers are currently performing scheduled maintenance on the Dashboard. Sorry for any inconvenience."] - Message to display
 * @param {string} [props.startDate="April 2"] - Start date of maintenance period
 * @param {string} [props.endDate="April 6"] - End date of maintenance period
 * @param {string} [props.periodFormat="{startDate} - {endDate}"] - Format string for the period display
 * @param {string} [props.iconColor="#3a5ed5"] - Color for the maintenance icon
 * @param {string} [props.cLogo] - Company logo URL
 * @param {string} [props.cBgImg] - Background image URL
 * @returns {JSX.Element} The maintenance page component
 */
const MaintenancePage = ({
  title = "Under maintenance",
  message = "Our engineers are currently performing scheduled maintenance on the Dashboard. Sorry for any inconvenience.",
  startDate = "April 2",
  endDate = "April 6",
  periodFormat = "{startDate} - {endDate}",
  iconColor = "#3a5ed5",
  cLogo,
  cBgImg,
}) => {
  // Use media query to detect mobile devices
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Format the message with line breaks if it contains \n
  const formattedMessage = message.split("\n").map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < message.split("\n").length - 1 && <br />}
    </React.Fragment>
  ));

  // Format the period display
  const periodDisplay = periodFormat
    .replace("{startDate}", startDate)
    .replace("{endDate}", endDate);

  return (
    <MaintenanceContainer
      style={{
        backgroundImage: cBgImg ? `url(${cBgImg})` : undefined,
      }}
    >
      <MaintenanceCard>
        {cLogo && (
          <LogoContainer>
            <img
              src={cLogo}
              alt="Company Logo"
              style={{
                maxWidth: "100%",
                height: "auto",
                maxHeight: isMobile ? "40px" : "60px",
              }}
            />
          </LogoContainer>
        )}

        <IconContainer>
          <HandymanIcon
            style={{
              fontSize: isMobile ? 42 : 55,
              color: iconColor,
            }}
          />
        </IconContainer>

        <MaintenanceTitle variant="h4">{title}</MaintenanceTitle>

        <MaintenanceMessage>{formattedMessage}</MaintenanceMessage>

        <MaintenancePeriod>
          Maintenance period: {periodDisplay}
        </MaintenancePeriod>
      </MaintenanceCard>
    </MaintenanceContainer>
  );
};

MaintenancePage.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  periodFormat: PropTypes.string,
  iconColor: PropTypes.string,
  cLogo: PropTypes.string,
  cBgImg: PropTypes.string,
};

export default MaintenancePage;
